import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { Signal } from "../core/Signal";

export type AssetKind = "texture" | "cubeTexture" | "hdr" | "gltf" | "audio" | "json";

export interface AssetDescriptor {
  key: string;
  kind: AssetKind;
  url: string | string[];
  options?: Record<string, unknown>;
}

interface AssetEntry {
  descriptor: AssetDescriptor;
  promise: Promise<unknown> | null;
  value: unknown;
  refCount: number;
  loaded: boolean;
  failed: boolean;
}

export interface AssetProgress {
  loaded: number;
  total: number;
  key: string;
  failed: boolean;
}

export class AssetService {
  readonly progress = new Signal<AssetProgress>();
  readonly onError = new Signal<{ key: string; error: unknown }>();

  private entries = new Map<string, AssetEntry>();
  private textureLoader = new THREE.TextureLoader();
  private cubeLoader = new THREE.CubeTextureLoader();
  private gltfLoader = new GLTFLoader();
  private rgbeLoader = new RGBELoader();
  private audioCtxFactory: () => AudioContext | null = () => null;

  setAudioContextFactory(fn: () => AudioContext | null): void {
    this.audioCtxFactory = fn;
  }

  register(manifest: AssetDescriptor[]): void {
    for (const d of manifest) {
      if (this.entries.has(d.key)) continue;
      this.entries.set(d.key, {
        descriptor: d,
        promise: null,
        value: undefined,
        refCount: 0,
        loaded: false,
        failed: false,
      });
    }
  }

  has(key: string): boolean {
    const e = this.entries.get(key);
    return !!e && e.loaded && !e.failed;
  }

  get<T = unknown>(key: string): T {
    const e = this.entries.get(key);
    if (!e || !e.loaded || e.failed) {
      throw new Error(`AssetService.get(${key}): not loaded`);
    }
    e.refCount += 1;
    return e.value as T;
  }

  tryGet<T = unknown>(key: string): T | null {
    const e = this.entries.get(key);
    if (!e || !e.loaded || e.failed) return null;
    e.refCount += 1;
    return e.value as T;
  }

  release(key: string): void {
    const e = this.entries.get(key);
    if (!e) return;
    e.refCount = Math.max(0, e.refCount - 1);
    if (e.refCount === 0) {
      this.disposeEntry(e);
    }
  }

  async loadAll(): Promise<void> {
    const total = this.entries.size;
    let loaded = 0;
    const tasks: Promise<unknown>[] = [];
    for (const [key, entry] of this.entries) {
      const task = this.loadEntry(entry)
        .catch((err) => {
          entry.failed = true;
          this.onError.emit({ key, error: err });
          console.warn(`[AssetService] failed to load "${key}":`, err);
        })
        .finally(() => {
          loaded += 1;
          this.progress.emit({ key, loaded, total, failed: entry.failed });
        });
      tasks.push(task);
    }
    await Promise.all(tasks);
  }

  load(key: string): Promise<unknown> {
    const e = this.entries.get(key);
    if (!e) return Promise.reject(new Error(`AssetService.load(${key}): not registered`));
    return this.loadEntry(e);
  }

  async replace(key: string, url: string | string[]): Promise<void> {
    const e = this.entries.get(key);
    if (!e) {
      throw new Error(`AssetService.replace(${key}): not registered`);
    }
    this.disposeEntry(e);
    e.descriptor = { ...e.descriptor, url };
    e.loaded = false;
    e.failed = false;
    e.value = undefined;
    e.promise = null;
    await this.loadEntry(e);
  }

  private loadEntry(entry: AssetEntry): Promise<unknown> {
    if (entry.promise) return entry.promise;
    if (entry.loaded) return Promise.resolve(entry.value);
    entry.promise = this.loadByKind(entry.descriptor)
      .then((value) => {
        entry.value = value;
        entry.loaded = true;
        return value;
      })
      .catch((err) => {
        entry.failed = true;
        throw err;
      });
    return entry.promise;
  }

  private loadByKind(d: AssetDescriptor): Promise<unknown> {
    switch (d.kind) {
      case "texture":
        return this.loadTexture(d.url as string, d.options);
      case "cubeTexture":
        return this.loadCubeTexture(d.url as string[]);
      case "hdr":
        return this.loadHdr(d.url as string);
      case "gltf":
        return this.loadGltf(d.url as string);
      case "audio":
        return this.loadAudio(d.url as string);
      case "json":
        return this.loadJson(d.url as string);
      default:
        return Promise.reject(new Error(`AssetService: unknown kind ${(d as AssetDescriptor).kind}`));
    }
  }

  private loadTexture(url: string, options?: Record<string, unknown>): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          if (options?.["wrap"] === "repeat") {
            tex.wrapS = THREE.RepeatWrapping;
            tex.wrapT = THREE.RepeatWrapping;
          }
          if (typeof options?.["anisotropy"] === "number") {
            tex.anisotropy = options["anisotropy"] as number;
          }
          resolve(tex);
        },
        undefined,
        (err) => reject(err)
      );
    });
  }

  private loadCubeTexture(urls: string[]): Promise<THREE.CubeTexture> {
    return new Promise((resolve, reject) => {
      this.cubeLoader.load(
        urls,
        (tex) => resolve(tex),
        undefined,
        (err) => reject(err)
      );
    });
  }

  private loadHdr(url: string): Promise<THREE.DataTexture> {
    return new Promise((resolve, reject) => {
      this.rgbeLoader.load(
        url,
        (tex) => {
          tex.mapping = THREE.EquirectangularReflectionMapping;
          resolve(tex);
        },
        undefined,
        (err) => reject(err)
      );
    });
  }

  private loadGltf(url: string): Promise<GLTF> {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        url,
        (gltf) => resolve(gltf),
        undefined,
        (err) => reject(err)
      );
    });
  }

  private async loadAudio(url: string): Promise<AudioBuffer> {
    const ctx = this.audioCtxFactory();
    if (!ctx) {
      throw new Error("AssetService.loadAudio: no AudioContext factory provided");
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    const arr = await res.arrayBuffer();
    return await ctx.decodeAudioData(arr);
  }

  private async loadJson(url: string): Promise<unknown> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return await res.json();
  }

  private disposeEntry(entry: AssetEntry): void {
    const v = entry.value;
    if (!v) return;
    if (v instanceof THREE.Texture) v.dispose();
    else if (v instanceof THREE.Material) v.dispose();
    else if (v instanceof THREE.BufferGeometry) v.dispose();
    else if (this.isGltf(v)) {
      this.disposeGltf(v as GLTF);
    }
    entry.value = undefined;
    entry.loaded = false;
  }

  private isGltf(v: unknown): boolean {
    return !!v && typeof v === "object" && "scene" in (v as Record<string, unknown>);
  }

  private disposeGltf(gltf: GLTF): void {
    gltf.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      mesh.geometry?.dispose?.();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose?.());
      else mat?.dispose?.();
    });
  }

  disposeAll(): void {
    for (const e of this.entries.values()) this.disposeEntry(e);
    this.entries.clear();
    this.progress.clear();
    this.onError.clear();
  }
}
